import React from 'react';

import { Species } from '../../api/fishrules/getSpeciesAtLocation';
import { ICoordinates } from '../../interfaces/geo';
import { InfoCard } from '../Cards/InfoCard/InfoCard';
import { Heading } from '../Heading/Heading';
import { WarningIcon } from '../Icons/WarningIcon';
import { Text } from '../Text/Text';

import { AggregateLimits } from './AggregateLimits';
import { getSeasonState } from './helpers';

interface IProps {
  species: Species;
  coordinates: ICoordinates;
  infoCardSize: 'lg' | 'sm';
  isProhibitedLocation: boolean;
  prohibitedMessage?: string;
}

export const RegulationCards = ({
  species,
  coordinates,
  infoCardSize,
  isProhibitedLocation,
  prohibitedMessage,
}: IProps) => {
  const isProhibited = isProhibitedLocation || species.prohibited === 1;
  const season = getSeasonState(species.seasons);

  if (isProhibited) {
    return (
      <div className="col-span-full mb-8 text-error-shade">
        <div className="mb-2">
          <WarningIcon />
        </div>
        <Text variant="sm" className="mb-0 font-semibold">
          {prohibitedMessage ||
            'It is unlawful to harvest, possess, land, purchase, sell, or exchange this species.'}
        </Text>
      </div>
    );
  }

  return (
    <>
      <InfoCard
        size={infoCardSize}
        modalContent={season.component}
        iconText={season.seasonLabel}
        title="Seasons"
        bgColorClassname={season.seasonLabel === 'Open' ? 'bg-success-tint' : 'bg-error-tint'}
        headingColorClassName="text-trench"
        dataTestid="seasons-info-icon"
      />
      {species.bag_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Bag limit
              </Heading>
              <Text className="mb-0">
                The maximum number of individual fish legally harvested per harvester per day. Bag
                limits are only for properly licensed anglers actively harvesting the species.
                People who are not actively harvesting or are not properly licensed (if a license is
                required) may NOT be counted for the purpose of bag limits.
              </Text>
            </>
          }
          iconText={species.bag_limit.toString()}
          title="Bag limit"
          bgColorClassname="bg-white"
        />
      )}
      {species.vessel_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Vessel limit
              </Heading>
              <Text className="mb-0">
                The maximum number of individual fish legally possessed aboard any vessel.
              </Text>
            </>
          }
          iconText={species.vessel_limit.toString()}
          title="Vessel limit"
          bgColorClassname="bg-white"
        />
      )}
      {species.trophy_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Memorable / trophy limits
              </Heading>
              <Text className="mb-0">
                The maximum number of trophy individuals legally harvested per harvester per day. A
                trophy is any individual of a length greater than the max size limit. Trophy
                individuals are included in the daily bag limit unless stated otherwise.
              </Text>
            </>
          }
          iconText={`${species.trophy_limit} > ${species.trophy_size}`}
          title="Memorable"
          bgColorClassname="bg-white"
        />
      )}
      {species.min_size !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Min size
              </Heading>
              <Text className="mb-0">
                No person shall harvest any individual fish less than the minimum size limit.
              </Text>
            </>
          }
          iconText={`${species.min_size}${species.measurement_unit_symbol}`}
          title="Min size"
          bgColorClassname="bg-white"
        />
      )}
      {species.max_size !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Max size
              </Heading>
              <Text className="mb-0">
                No person shall harvest any individual fish greater than the maximum size except as
                permitted.
              </Text>
            </>
          }
          iconText={`${species.max_size}${species.measurement_unit_symbol}`}
          title="Max size"
          bgColorClassname="bg-white"
        />
      )}
      {species.measurement_name && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                {`Measurement: ${species.measurement_name}`}
              </Heading>
              <Text className="mb-0">{species.measurement_description}</Text>
            </>
          }
          iconText={species.measurement_name}
          title="Measurement"
          bgColorClassname="bg-white"
        />
      )}
      {species.gear_name && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                {species.gear_name}
              </Heading>
              <Text className="mb-0">{species.gear_description}</Text>
            </>
          }
          iconText=""
          title="Special gear"
          bgColorClassname="bg-white"
        />
      )}
      {species.aggregate_limit !== null && (
        <InfoCard
          size={infoCardSize}
          modalContent={<AggregateLimits coordinates={coordinates} species={species} />}
          iconText={species.aggregate_limit.toString()}
          title="Aggregate"
          bgColorClassname="bg-white"
        />
      )}
      {species.keep_intact === 1 && (
        <InfoCard
          size={infoCardSize}
          modalContent={
            <>
              <Heading variant="sm" className="mb-4">
                Keep intact
              </Heading>
              <Text className="mb-0">
                In waters where minimum-length or slot-size limits apply, game fish may not be
                filleted, nor their head or tail fin removed until the angler has completed fishing
                for the day.
              </Text>
            </>
          }
          iconText="Keep intact"
          title="Keep intact"
          bgColorClassname="bg-white"
        />
      )}
    </>
  );
};
